import React from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { useLocation } from '@reach/router';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Carousel from '@components/shared/Carousel';
import { P } from '@components/shared/Typography';
import {
  EVENINGSTANDARD_BLUE_LOGO_SVG,
  GUARDIAN_BLUE_LOGO_SVG,
  VOGUE_BLUE_LOGO_SVG,
  WIRED_BLUE_LOGO_SVG,
  HEALTHLINE_LOGO_SVG,
  POPSUGAR_LOGO_SVG,
  REFINERY29_LOGO_SVG,
  FORBES_LOGO_SVG,
  GLAMOUR_LOGO_SVG,
  TECH_CRUNCH_LOGO_SVG,
  METRO_LOGO_SVG,
  ELVIE_BLUE_LOGO_SVG,
  WILD_NUTRITION_BLUE_LOGO_SVG,
  SKIN_ME_BLUE_LOGO_SVG,
  MYOOVI_BLUE_LOGO_SVG,
  TIMES_BLUE_LOGO_SVG
} from '@constants';
import { PageSlice } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { replaceText } from '@utils/handleTextReplacement';
import Colors from './shared/Colors';
import useDesktop from './shared/useDesktop';

const carouselOptions = {
  autoPlay: 2000,
  groupCells: false,
  pauseAutoPlayOnHover: false,
  cellAlign: 'center',
  draggable: true,
  watchCSS: false
};

const quotesCarouselOptions = {
  autoPlay: false,
  groupCells: false,
  cellAlign: 'center',
  draggable: false,
  pageDots: true,
  wrapAround: true
};

interface Props {
  slice: PageSlice;
}

const FeaturedIn: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const { country } = useCountry();
  const location = useLocation();
  const isMobile = !useDesktop(1024);
  let featuredLogos = [GUARDIAN_BLUE_LOGO_SVG, VOGUE_BLUE_LOGO_SVG, WIRED_BLUE_LOGO_SVG, TIMES_BLUE_LOGO_SVG, EVENINGSTANDARD_BLUE_LOGO_SVG];
  const partneringLogos = [ELVIE_BLUE_LOGO_SVG, MYOOVI_BLUE_LOGO_SVG, SKIN_ME_BLUE_LOGO_SVG, WILD_NUTRITION_BLUE_LOGO_SVG];
  const vmsFeaturedLogos = [TECH_CRUNCH_LOGO_SVG, GLAMOUR_LOGO_SVG, METRO_LOGO_SVG, REFINERY29_LOGO_SVG];
  const { featured_title, type, background_color, text_color, background_gradient } = slice.primary;
  const { items } = slice;

  if (country === CountryCode.US) {
    featuredLogos = [FORBES_LOGO_SVG, REFINERY29_LOGO_SVG, GLAMOUR_LOGO_SVG, POPSUGAR_LOGO_SVG, HEALTHLINE_LOGO_SVG];
  }

  const getDefaultView = () => (
    <Container $background={background_color ?? ''}>
      <Inner>
        <MobileTitle $textColor={text_color ?? ''}>{featured_title.text ?? t('components.as-recommended')}</MobileTitle>
        <CarouselWrap>
          <Carousel options={carouselOptions}>
            <CarouselLogo>
              {!isMobile && <DesktopTitle $textColor={text_color ?? ''}>{featured_title.text ?? t('components.as-recommended')}</DesktopTitle>}
            </CarouselLogo>
            {featuredLogos.map((logo, index) => (
              <CarouselLogo key={index}>
                <img src={logo} alt="Featured logo" />
              </CarouselLogo>
            ))}
          </Carousel>
        </CarouselWrap>
      </Inner>
    </Container>
  );

  const getDetailedView = (logos: string[], hideQuotes: boolean = false) => (
    <Container $background={background_color ?? ''} $backgroundGradient={background_gradient?.text}>
      <Inner $isDetailedType={true} $justifyContent="center">
        {!hideQuotes && (
          <>
            {isMobile ? (
              <CarouselWrap>
                <Carousel options={quotesCarouselOptions}>
                  {items &&
                    items.map((item, index) => (
                      <ColumnWrap $textColor={text_color ?? ''} key={index}>
                        <CarouselLogo>
                          <GatsbyImage image={item.logo.gatsbyImageData} alt={item.logo.alt ?? ''} objectFit="contain" />
                        </CarouselLogo>
                        <b>{item.quote.text}</b>
                      </ColumnWrap>
                    ))}
                </Carousel>
              </CarouselWrap>
            ) : (
              <>
                {items &&
                  items.map((item, index) => (
                    <ColumnWrap $textColor={text_color ?? ''} $maxWidth={600 / items.length} key={index}>
                      <CarouselLogo>
                        <GatsbyImage image={item.logo.gatsbyImageData} alt={item.logo.alt ?? ''} objectFit="contain" />
                      </CarouselLogo>
                      <b>{replaceText(item.quote.text, country, location)}</b>
                    </ColumnWrap>
                  ))}
              </>
            )}
          </>
        )}
        <div style={{ marginBottom: isMobile && !hideQuotes ? '70px' : '0' }} />
        {isMobile ? (
          <CarouselWrap>
            <BoldText $textColor={text_color ?? ''}>As recommended by</BoldText>
            <div style={{ marginBottom: '25px' }} />
            <Carousel options={carouselOptions}>
              {logos.map((logo, index) => (
                <CarouselLogo key={index}>
                  <img src={logo} alt="Featured logo" />
                </CarouselLogo>
              ))}
            </Carousel>
          </CarouselWrap>
        ) : (
          <ColumnWrap>
            <BoldText $textColor={text_color ?? ''}>As recommended by</BoldText>
            <LogosWrap>
              {logos.map((logo, index) => (
                <CarouselLogo key={index} $isDetailedType={true}>
                  <img src={logo} alt="Featured logo" />
                </CarouselLogo>
              ))}
            </LogosWrap>
          </ColumnWrap>
        )}
      </Inner>
    </Container>
  );

  const getPartneringView = () => (
    <Container $background={background_color ?? ''}>
      <Inner style={{ columnGap: '0', justifyContent: 'center', marginBottom: '0' }}>
        <MobileTitle $textColor={text_color ?? ''}>{featured_title.text}</MobileTitle>
        <CarouselWrap $desktopWidth="70%">
          <Carousel options={carouselOptions}>
            <CarouselLogo>{!isMobile && <DesktopTitle $textColor={text_color ?? ''}>{featured_title.text}</DesktopTitle>}</CarouselLogo>
            {partneringLogos.map((logo, index) => (
              <CarouselLogo key={index}>
                <img src={logo} alt="Featured logo" />
              </CarouselLogo>
            ))}
          </Carousel>
        </CarouselWrap>
        <AdditionalText $textColor={text_color ?? ''}>and many more...</AdditionalText>
      </Inner>
    </Container>
  );

  switch (type) {
    case 'detailed':
      return getDetailedView(featuredLogos);
    case 'partnering':
      return getPartneringView();
    case 'vms':
      return getDetailedView(vmsFeaturedLogos, isMobile);
    case 'default':
    default:
      return getDefaultView();
  }
};

export default FeaturedIn;

interface StyleProps {
  $background?: string;
  $backgroundGradient?: string;
  $desktopWidth?: string;
  $justifyContent?: string;
  $isDetailedType?: boolean;
  $textColor?: string;
  $maxWidth?: number;
}

const Container = styled.div<StyleProps>`
  background: ${(props) => (props.$background ? `${props.$background}` : 'transparent')};
  background-image: ${(props) => (props.$backgroundGradient ? props.$backgroundGradient : 'none')};
  width: 100%;
  padding: 60px 0;

  @media (max-width: 767px) {
    padding: 30px 0;
  }
`;

const Inner = styled.div<StyleProps>`
  display: flex;
  align-items: ${(props) => (props.$isDetailedType ? 'start' : 'center')};
  justify-content: ${({ $justifyContent }) => $justifyContent ?? 'start'};
  flex-direction: row;
  max-width: 1200px;
  width: 100%;
  column-gap: 40px;
  margin: 0 auto;

  @media (max-width: 1200px) {
    flex-direction: column;
    margin-bottom: 20px;
  }
`;

const BoldText = styled.div<StyleProps>`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${(props) => (props.$textColor ? `${props.$textColor}` : Colors.forestGreen)};

  @media (max-width: 1200px) {
    font-weight: 700;
    font-size: 12px;
    line-height: 140%;
    text-align: center;
    align-self: center;
  }
`;

const CarouselWrap = styled.div<StyleProps>`
  width: ${(props) => (props.$desktopWidth ? `${props.$desktopWidth}` : '100%')};

  @media (max-width: 1200px) {
    width: 100%;
  }
`;

const Title = styled(P)<StyleProps>`
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 1.5px;
  white-space: nowrap;
  text-transform: uppercase;
  color: ${(props) => (props.$textColor ? `${props.$textColor}` : Colors.forestGreen)};

  @media (max-width: 1200px) {
    margin-bottom: 20px;
  }
`;

const MobileTitle = styled(Title)`
  display: none;
  @media (max-width: 1024px) {
    display: block;
  }
`;

const DesktopTitle = styled(Title)`
  @media (max-width: 1024px) {
    display: none;
  }
`;

const CarouselLogo = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isDetailedType ? 'start' : 'center')};
  height: 40px;
  margin-left: ${(props) => (props.$isDetailedType ? '0' : '40px')};
  max-width: 200px;

  & img {
    height: 100%;
    width: auto;
    max-height: 22px;
  }

  &:first-child {
    margin-left: 0;
  }

  &:empty {
    display: none;
  }

  path {
    fill: ${Colors.navyBlue};
  }

  .red-apple path {
    fill: #ed6866;
  }

  @media (max-width: 1200px) {
    padding: 0 30px;
    margin-left: 0;
    align-self: center;
  }
`;

const ColumnWrap = styled.div<StyleProps>`
  display: flex;
  align-items: start;
  flex-direction: column;
  gap: 20px;
  max-width: ${(props) => (props.$maxWidth ? `${props.$maxWidth}px` : '300px')};
  margin: 0 30px;

  b {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 150%;
    color: ${(props) => (props.$textColor ? `${props.$textColor}` : Colors.forestGreen)};
  }

  @media (max-width: 1200px) {
    display: flex;
    padding: 0 90px 30px 90px;
    align-self: center;
    max-width: fit-content;

    b {
      text-align: center;
    }
  }

  @media (max-width: 768px) {
    margin: 0;
    padding: 0 20px;
  }
`;

const LogosWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 20px;
  column-gap: 20px;

  @media (max-width: 1200px) {
    padding: 0 20px;
    margin-left: 0;
  }
`;

const AdditionalText = styled(Title)`
  @media (max-width: 1200px) {
    margin-top: 20px;
    margin-bottom: 0;
  }
`;
